import { useState, useRef, useEffect } from "react";
import { createUseStyles } from "react-jss";
import produce from "immer";

import ModeSelect from "../components/ModeSelect";
import Input from "../components/Input";
import Button from "../components/Button";
import { getPins } from "../../lib/player/src/Customisation";
import TopBar from "../components/refresh/TopBar";

const useStyles = createUseStyles(theme => ({
  container: {
    width: "100%",
    height: "100vh",
    display: "flex",
    "flex-direction": "column",
    overflow: "hidden",
  },
  mainContainer: {
    display: "flex",
    flex: 1,
    "flex-direction": "column",
    overflow: "hidden",
    "background-color": "#333",
    height: "100%",
  },
  centerer: {
    "background-color": "#333",
    flex: 1,
    height: "100%",
    display: "flex",
    "justify-content": "center",
    "align-items": "center",
  },
  enabledWarning: {
    display: "flex",
    "flex-direction": "column",
    "align-items": "center",
    "& h1": {
      color: "white",
    },
  },
  preview: {
    "background-size": "cover",
    "background-repeat": "no-repeat",
    "background-position": "center",
    "background-image": props => props.background,
    width: "100%",
    height: "100%",
  },
  options: {
    display: "flex",
    "flex-direction": "row",
    "align-items": "center",
    "overflow-y": "hidden",
    "overflow-x": "auto",
  },
  label: {
    color: "white",
    display: "flex",
    "flex-direction": "column",
    padding: [10, 0, 10, 15],
    "min-width": 200,
    "flex-shrink": 0,
  },
  buttons: {
    display: "flex",
    "flex-direction": "row",
    height: 36,
    margin: [10, 0],
    "& button": {
      background: "#575757",
      color: "white",
      outline: "none",
      border: "none",
      "border-radius": 7,
      padding: "4px 15px",
      marginLeft: 5,
      cursor: "pointer",
    },
  },
  buttonActive: { background: `${theme.colorSecondary} !important` },
  decks: {
    position: "absolute",
    top: "calc(50%)",
    transform: "translateY(-50%)",
    "-webkit-backface-visibility": "hidden",
    zIndex: 1,
  },
  decksActive: {
    extend: "decks",
    zIndex: 10,
  },
  deck: {
    position: "relative",
  },
  deckHidden: {
    extend: "deck",
    display: "none",
  },
  hotspot: {
    "background-image": props => `url("${props.pins.default}")`,
    backgroundSize: "cover",
    // cursor: "pointer",
    width: 25,
    height: 25,
    position: "absolute",
    "transition-duration": "0.4s",
    overflow: "hidden",

    "&:hover": {
      "background-image": props => `url("${props.pins.hover}")`,
      backgroundSize: "cover",
      height: 36,
      marginTop: "-11px",
      overflow: "visible",
    },
  },
  hotspotContent: {
    opacity: 0,
    height: "175px",
    width: "225px",
    position: "absolute",
    top: "-175px",
    left: "50%",
    transform: "translateX(-50%)",
    "transition-duration": "1s",
  },
  hotspotContentActive: {
    extend: "hotspotContent",
    opacity: 1,
  },
  hotspotImage: {
    height: "100%",
  },
  hotspotPlay: {
    position: "absolute",
    top: 5,
    right: 5,
    width: 27,
    opacity: 0.75,
    color: "white",
    "&:hover": {
      top: 4,
      opacity: 1,
    },
  },
  backgroundHidden: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    zIndex: 9,
  },
}));

const Hotspots = ({
  tour,
  onUpdateTour,
  onEmbedTour,
  onSaveTour,
  onExit,
  dirty,
  onTourSettings,
}) => {
  const [hotspotPage, setHotspotPage] = useState(
    tour.data?.options?.hotspotPage || {
      show: false,
      background: 'url("")',
      direction: "left",
      heightMultiplier: 0,
      externalAdjust: 0,
      newScaling: true,
    },
  );
  const [size, setSize] = useState({ width: 0, height: 0 });
  const observer = useRef(
    new ResizeObserver(entries => {
      const { width, height } = entries[0].contentRect;
      setSize({ width, height });
    }),
  );
  const imgRef = useRef();
  const classes = useStyles({
    background: hotspotPage.background,
    pins: getPins(),
  });

  useEffect(() => {
    const observerCopy = observer.current;
    const imgRefCopy = imgRef.current;
    if (imgRefCopy) {
      observerCopy.observe(imgRefCopy);
    }

    return () => {
      if (imgRefCopy) {
        observerCopy.unobserve(imgRefCopy);
      }
    };
  });

  const tourJSON = JSON.stringify(tour);
  const hotspotPageJSON = JSON.stringify(hotspotPage);

  // for saving
  useEffect(() => {
    const tourUnJSON = JSON.parse(tourJSON);
    const hotspotPageUnJSON = JSON.parse(hotspotPageJSON);
    if (
      JSON.stringify(tourUnJSON.data?.options?.hotspotPage || {}) !==
      hotspotPageJSON
    ) {
      onUpdateTour(
        produce(tourUnJSON, draft => {
          draft.data.options.hotspotPage = hotspotPageUnJSON;
          draft.data.iframeOptions.hotspotPage = hotspotPageUnJSON;
        }),
      );
    }
  }, [onUpdateTour, tourJSON, hotspotPageJSON]);

  const calcMargin = (k, total) => {
    const level = k - Math.round((total + 1) / 2);

    const externalAdjust = hotspotPage.externalAdjust || 0;
    const heightMultiplier = hotspotPage.heightMultiplier * 36 || 30;

    const hotspotHeight =
      ((size.width > size.height ? size.width : size.height) / 970) ** 0.92;

    const movement =
      hotspotHeight * heightMultiplier * level + externalAdjust * hotspotHeight;
    return movement;
  };

  // calculate the width of the boat
  // NOTE: the boat image is placed on the div using 'cover' so
  // it's size is dependant on the larger of the width or height
  const externalAdjust = hotspotPage.externalAdjust || 0;
  const heightMultiplier = hotspotPage.heightMultiplier || 0;
  let boatWidth = 0;
  let hotspotHeight = 0;
  let hotspotHeightAdjust = 0;
  if (size.width > size.height) {
    boatWidth = size.width * 0.7;
    hotspotHeight = size.width * 0.06;
    hotspotHeightAdjust = -size.width * 0.02 - 20;
  } else {
    boatWidth = size.height * 0.7;
    hotspotHeight = size.height * 0.06;
    hotspotHeightAdjust = -size.height * 0.02 - 20;
  }

  if (hotspotPage.show) {
    return (
      <div className={classes.container}>
        <TopBar
          links={{ onTourSettings, onEmbedTour, onSaveTour, dirty, onExit }}
        />
        <div className={classes.mainContainer}>
          <div className={classes.options}>
            <label className={classes.label}>
              Background Image:
              <Input
                value={hotspotPage.background.slice(5, -2)}
                onChange={v =>
                  setHotspotPage(h => ({
                    ...h,
                    background: `url("${v}")`,
                  }))
                }
              />
            </label>
            <label className={classes.label}>
              Direction:
              <div className={classes.buttons}>
                <button
                  className={
                    hotspotPage.direction === "left" ? classes.buttonActive : ""
                  }
                  onClick={() =>
                    setHotspotPage(h => ({
                      ...h,
                      direction: "left",
                    }))
                  }
                >
                  Left to Right
                </button>
                <button
                  className={
                    hotspotPage.direction === "right"
                      ? classes.buttonActive
                      : ""
                  }
                  onClick={() =>
                    setHotspotPage(h => ({
                      ...h,
                      direction: "right",
                    }))
                  }
                >
                  Right to Left
                </button>
              </div>
            </label>
            <label className={classes.label}>
              Image Height Mutliplier:
              <Input
                type="number"
                value={hotspotPage.heightMultiplier}
                onChange={v =>
                  setHotspotPage(h => ({
                    ...h,
                    heightMultiplier: v,
                  }))
                }
              />
            </label>
            <label className={classes.label}>
              External Image Adjustment:
              <Input
                type="number"
                value={hotspotPage.externalAdjust}
                onChange={v =>
                  setHotspotPage(h => ({
                    ...h,
                    externalAdjust: v,
                  }))
                }
              />
            </label>
            <label className={classes.label}>
              Disable Hotspot Page:
              <div className={classes.buttons}>
                <button
                  onClick={() =>
                    setHotspotPage({
                      show: false,
                      background: 'url("")',
                      direction: "left",
                      heightMultiplier: 0,
                      externalAdjust: 0,
                      newScaling: true,
                    })
                  }
                >
                  Disable
                </button>
              </div>
            </label>
          </div>
          <div className={classes.preview} ref={imgRef}>
            <div
              className={classes.decksActive}
              style={{
                left: `${(size.width - boatWidth) / 2}px`,
                width: `${boatWidth}px`,
                height: hotspotPage.newScaling ? 0 : "auto",
              }}
            >
              {tour.data.floorplans &&
                tour.data.floorplans.map((d, k) => (
                  <div
                    key={d.id}
                    className={classes.deck}
                    style={{
                      top: `${
                        (hotspotPage.newScaling
                          ? calcMargin(k, tour.data.floorplans.length)
                          : (hotspotHeight * k +
                              hotspotHeightAdjust +
                              externalAdjust) *
                            heightMultiplier) + 42
                      }px`,
                    }}
                  >
                    {d.hotspots.map((h, j) => (
                      <div
                        key={`${d.id}_${j}`}
                        style={
                          hotspotPage.direction === "left"
                            ? { left: h.x * boatWidth }
                            : { right: h.x * boatWidth }
                        }
                        className={classes.hotspot}
                      />
                    ))}
                  </div>
                ))}
            </div>
          </div>
        </div>
        <ModeSelect />
      </div>
    );
  } else {
    return (
      <div className={classes.container}>
        <TopBar
          links={{ onTourSettings, onEmbedTour, onSaveTour, dirty, onExit }}
        />
        <div className={classes.centerer}>
          <div className={classes.enabledWarning}>
            <h1>Hotspot Page is not enabled</h1>
            <Button
              onClick={() =>
                setHotspotPage(h => ({
                  ...h,
                  show: true,
                }))
              }
            >
              Enable
            </Button>
          </div>
        </div>
        <ModeSelect />
      </div>
    );
  }
};

export default Hotspots;
